import React, { useEffect, useMemo, useCallback, useState, type ReactNode } from "react";
import { Command as CommandIcon, Search } from "lucide-react";
import { resolveIcon } from "../utils/icons";
import { CommandPalette } from "./CommandPalette";
import { Sidebar } from "./Sidebar";
import type { CategoryInfo } from "../types";

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function mixHexColors(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  const r = Math.round(ar * t + br * (1 - t));
  const g = Math.round(ag * t + bg * (1 - t));
  const bl = Math.round(ab * t + bb * (1 - t));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${bl.toString(16).padStart(2, "0")}`;
}

function EmptyState({ categories, onOpen }: {
  readonly categories: ReadonlyArray<CategoryInfo>;
  readonly onOpen: (name: string) => void;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-center h-full w-full">
      <div className="flex flex-col gap-0.5 w-56">
        <div className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-medium px-3 mb-1">Categories</div>
        {categories.map((category) => {
          const Icon = resolveIcon(category.icon);
          return (
            <button key={category.name} onClick={() => onOpen(category.name)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-transparent hover:bg-card/80 transition-all duration-150 border border-transparent hover:border-border/40 text-left cursor-pointer group">
              {Icon ? <Icon size={13} className="text-muted-foreground/70 group-hover:text-foreground/80 shrink-0 transition-colors" /> : <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: category.color }} />}
              <span className="flex-1 text-foreground/90 text-[13px] group-hover:text-foreground transition-colors">{category.name}</span>
            </button>
          );
        })}
        <div className="flex items-center justify-center gap-1.5 mt-4 text-[10px] text-muted-foreground/40">
          <CommandIcon size={10} /><span>K to search</span>
        </div>
      </div>
    </div>
  );
}

function TopBar({ title, description, onSearch, categoryColor }: {
  readonly title: string;
  readonly description: string;
  readonly onSearch: () => void;
  readonly categoryColor: string | undefined;
}): React.ReactElement {
  return (
    <div
      className="h-11 shrink-0 flex items-center border-b border-border/50 px-4 gap-4 transition-colors duration-300"
      style={{ backgroundColor: categoryColor ? mixHexColors(categoryColor, "#1c1c1e", 0.15) : undefined }}
    >
      <div className="flex items-center gap-2 shrink-0">
        <div className="w-5 h-5 rounded bg-card border border-border/50 flex items-center justify-center">
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none" className="text-foreground/80">
            <path d="M2 4L8 1L14 4V12L8 15L2 12V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          </svg>
        </div>
        <span className="text-[13px] font-semibold text-foreground tracking-tight">{title}</span>
        {description && (
          <span className="text-[11px] text-muted-foreground/70 truncate max-w-[200px]">{description}</span>
        )}
      </div>

      <div className="flex-1 flex justify-center">
        <button
          onClick={onSearch}
          className="flex items-center gap-2 px-3 py-1 rounded-lg border border-border/40 bg-card/40 hover:bg-card/70 transition-colors cursor-pointer text-muted-foreground/60 hover:text-muted-foreground/80 max-w-[360px] min-w-[220px] w-full"
        >
          <Search size={11} className="shrink-0" />
          <span className="text-[11px] truncate flex-1 text-left">Search...</span>
          <div className="flex items-center gap-0.5 shrink-0">
            <kbd className="text-[9px] bg-background/60 px-1 py-px rounded border border-border/40 font-mono text-muted-foreground/60">
              <CommandIcon size={8} className="inline" />
            </kbd>
            <kbd className="text-[9px] bg-background/60 px-1 py-px rounded border border-border/40 font-mono text-muted-foreground/60">K</kbd>
          </div>
        </button>
      </div>

      <div className="w-[100px] shrink-0" />
    </div>
  );
}

interface SidebarNavigationItem {
  readonly categoryName: string;
  readonly tabId: string;
}

function isNavigableCategory(category: CategoryInfo, collapsedCategories: ReadonlySet<string>): boolean {
  return !collapsedCategories.has(category.name) && category.type !== "files";
}

function buildSidebarNavigationItems(
  categories: ReadonlyArray<CategoryInfo>,
  collapsedCategories: ReadonlySet<string>,
): readonly SidebarNavigationItem[] {
  return categories
    .filter((category) => isNavigableCategory(category, collapsedCategories))
    .flatMap((category) =>
      category.tabs.map((tab) => ({ categoryName: category.name, tabId: tab.id })),
    );
}

function isTerminalFocused(): boolean {
  const activeElement = document.activeElement;
  return activeElement != null && activeElement.closest(".xterm") != null;
}

function toggleSetItem<T>(source: ReadonlySet<T>, item: T): Set<T> {
  if (source.has(item)) return new Set([...source].filter((entry) => entry !== item));
  return new Set([...source, item]);
}

function navigateSidebarItem(
  sidebarItems: readonly SidebarNavigationItem[],
  activeTabId: string,
  activeCategory: string,
  selectCategory: (name: string) => void,
  selectTab: (id: string) => void,
  delta: number,
): void {
  if (sidebarItems.length === 0) return;
  const currentIndex = sidebarItems.findIndex((item) => item.tabId === activeTabId);
  const nextIndex = currentIndex === -1 ? 0 : (currentIndex + delta + sidebarItems.length) % sidebarItems.length;
  const nextItem = sidebarItems[nextIndex]!;
  if (nextItem.categoryName !== activeCategory) selectCategory(nextItem.categoryName);
  selectTab(nextItem.tabId);
}

function handleCommandPaletteKey(
  event: KeyboardEvent,
  isModifierPressed: boolean,
  commandPaletteOpen: boolean,
  setCommandPaletteOpen: (open: boolean | ((prev: boolean) => boolean)) => void,
): boolean {
  if (isModifierPressed && (event.key === "k" || event.key === "p")) { event.preventDefault(); setCommandPaletteOpen((prev: boolean) => !prev); return true; }
  if (event.key === "Escape" && commandPaletteOpen) { setCommandPaletteOpen(false); return true; }
  return false;
}

function handleCategorySwitchKey(
  event: KeyboardEvent,
  isModifierPressed: boolean,
  categories: ReadonlyArray<CategoryInfo>,
  selectCategory: (name: string) => void,
): boolean {
  if (!isModifierPressed || event.key < "1" || event.key > "9") return false;
  event.preventDefault();
  const categoryIndex = parseInt(event.key, 10) - 1;
  if (categoryIndex < categories.length) selectCategory(categories[categoryIndex]!.name);
  return true;
}

function handleItemSwitchKey(
  event: KeyboardEvent,
  isModifierPressed: boolean,
  sidebarItems: readonly SidebarNavigationItem[],
  activeTabId: string,
  activeCategory: string,
  selectCategory: (name: string) => void,
  selectTab: (id: string) => void,
): boolean {
  // Cmd+[ / Cmd+] or Cmd+Shift+[ / Cmd+Shift+]
  if (isModifierPressed && (event.key === "[" || event.key === "]" || event.key === "{" || event.key === "}")) {
    event.preventDefault();
    const isNext = event.key === "]" || event.key === "}";
    navigateSidebarItem(sidebarItems, activeTabId, activeCategory, selectCategory, selectTab, isNext ? 1 : -1);
    return true;
  }

  // Cmd/Ctrl+T → Next item
  if (isModifierPressed && event.key === "t") {
    event.preventDefault();
    navigateSidebarItem(sidebarItems, activeTabId, activeCategory, selectCategory, selectTab, 1);
    return true;
  }

  // Ctrl+Tab / Ctrl+Shift+Tab
  if (event.ctrlKey && event.key === "Tab") {
    event.preventDefault();
    navigateSidebarItem(sidebarItems, activeTabId, activeCategory, selectCategory, selectTab, event.shiftKey ? -1 : 1);
    return true;
  }

  return false;
}

function handleArrowNavigation(
  event: KeyboardEvent,
  commandPaletteOpen: boolean,
  sidebarItems: readonly SidebarNavigationItem[],
  activeTabId: string,
  activeCategory: string,
  selectCategory: (name: string) => void,
  selectTab: (id: string) => void,
): boolean {
  const isArrowKey = event.key === "ArrowUp" || event.key === "ArrowDown";
  if (!isArrowKey || isTerminalFocused() || commandPaletteOpen || sidebarItems.length === 0) return false;
  event.preventDefault();
  const delta = event.key === "ArrowDown" ? 1 : -1;
  navigateSidebarItem(sidebarItems, activeTabId, activeCategory, selectCategory, selectTab, delta);
  return true;
}

function useKeyboardShortcuts({
  commandPaletteOpen,
  setCommandPaletteOpen,
  categories,
  selectCategory,
  activeTabId,
  selectTab,
  sidebarItems,
  activeCategory,
}: {
  readonly commandPaletteOpen: boolean;
  readonly setCommandPaletteOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
  readonly categories: ReadonlyArray<CategoryInfo>;
  readonly selectCategory: (name: string) => void;
  readonly activeTabId: string;
  readonly selectTab: (id: string) => void;
  readonly sidebarItems: readonly SidebarNavigationItem[];
  readonly activeCategory: string;
}): void {
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      const isModifierPressed = event.metaKey || event.ctrlKey;
      if (handleCommandPaletteKey(event, isModifierPressed, commandPaletteOpen, setCommandPaletteOpen)) return;
      if (handleCategorySwitchKey(event, isModifierPressed, categories, selectCategory)) return;
      if (handleItemSwitchKey(event, isModifierPressed, sidebarItems, activeTabId, activeCategory, selectCategory, selectTab)) return;
      handleArrowNavigation(event, commandPaletteOpen, sidebarItems, activeTabId, activeCategory, selectCategory, selectTab);
    };

    // Capture phase fires before browser default shortcut processing,
    // allowing us to intercept Cmd+T, Cmd+P, Ctrl+Tab in PWA standalone mode
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [commandPaletteOpen, categories, selectCategory, activeTabId, selectTab, sidebarItems, activeCategory, setCommandPaletteOpen]);
}

export function WmuxApp(props: {
  readonly title: string;
  readonly description: string;
  readonly categories: ReadonlyArray<CategoryInfo>;
  readonly activeCategory: string;
  readonly activeTabId: string;
  readonly children?: ReactNode;
  readonly onSelectCategory: { readonly mutate: (name: string) => void };
  readonly onSelectTab: { readonly mutate: (id: string) => void };
  readonly onStartProcess: { readonly mutate: (id: string) => void };
  readonly onStopProcess: { readonly mutate: (id: string) => void };
  readonly onRestartProcess: { readonly mutate: (id: string) => void };
  readonly onToggleDir: { readonly mutate: (path: string) => void };
  readonly onOpenFile: { readonly mutate: (path: string) => void };
  readonly onCloseFile: { readonly mutate: (path: string) => void };
}): React.ReactElement {
  const { title, description, categories, activeCategory, activeTabId, children } = props;
  const selectCategory = props.onSelectCategory.mutate;
  const selectTab = props.onSelectTab.mutate;
  const startProcess = props.onStartProcess.mutate;
  const stopProcess = props.onStopProcess.mutate;
  const restartProcess = props.onRestartProcess.mutate;
  const toggleDir = props.onToggleDir.mutate;
  const openFile = props.onOpenFile.mutate;
  const closeFile = props.onCloseFile.mutate;

  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const activeCategoryInfo = categories.find((c) => c.name === activeCategory);
  const activeTabName = useMemo(() => {
    if (!activeCategoryInfo) return null;
    if (activeCategoryInfo.type === "files") {
      const file = (activeCategoryInfo.openFiles ?? []).find((f) => `file::${f.path}` === activeTabId);
      return file?.name ?? null;
    }
    return activeCategoryInfo.tabs.find((t) => t.id === activeTabId)?.name ?? null;
  }, [activeCategoryInfo, activeTabId]);

  // Update document title and PWA theme-color tint based on active tab
  useEffect(() => {
    const parts = [activeTabName, activeCategory, title].filter(Boolean);
    document.title = parts.length > 0 ? parts.join(" — ") : "wmux";
  }, [activeTabName, activeCategory, title]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const color = activeCategoryInfo?.color;
    meta.setAttribute("content", color ? mixHexColors(color, "#1c1c1e", 0.15) : "#1c1c1e");
  }, [activeCategoryInfo?.color]);

  const registry = useMemo(() => {
    const map = new Map<string, ReactNode>();
    for (const child of React.Children.toArray(children) as React.ReactElement[]) {
      const id = (child.props as { id?: string }).id;
      if (id) map.set(id, child);
    }
    return map;
  }, [children]);

  const sidebarItems = useMemo(
    () => buildSidebarNavigationItems(categories, collapsedCategories),
    [categories, collapsedCategories],
  );

  useKeyboardShortcuts({
    commandPaletteOpen,
    setCommandPaletteOpen,
    categories,
    selectCategory,
    activeTabId,
    selectTab,
    sidebarItems,
    activeCategory,
  });

  const toggleCollapse = useCallback((name: string) => {
    setCollapsedCategories((prev) => toggleSetItem(prev, name));
  }, []);

  return (
    <div className="flex flex-col h-screen w-screen bg-background text-foreground font-sans">
      <TopBar title={title} description={description} onSearch={() => setCommandPaletteOpen(true)} categoryColor={activeCategoryInfo?.color} />

      <div className="flex flex-1 min-h-0">
        <Sidebar
          categories={categories}
          activeCategory={activeCategory}
          activeTabId={activeTabId}
          collapsedCategories={collapsedCategories}
          onSelectCategory={selectCategory}
          onToggleCollapse={toggleCollapse}
          onSelectTab={selectTab}
          onToggleDir={toggleDir}
          onOpenFile={openFile}
          onStartProcess={startProcess}
          onStopProcess={stopProcess}
          onRestartProcess={restartProcess}
        />

        <div className="flex-1 flex flex-col min-w-0 relative">
          <div className="flex-1 min-h-0 relative bg-background">
            {[...registry.entries()].map(([id, child]) => (
              <div
                key={id}
                className="absolute inset-0"
                style={{ visibility: id === activeTabId ? "visible" : "hidden", zIndex: id === activeTabId ? 1 : 0 }}
              >
                {child}
              </div>
            ))}

            {!activeCategory && (
              <div className="absolute inset-0 z-10">
                <EmptyState categories={categories} onOpen={selectCategory} />
              </div>
            )}
          </div>
        </div>
      </div>

      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        categories={categories}
        activeTabId={activeTabId}
        onSelectCategory={selectCategory}
        onSelectTab={selectTab}
        onStartProcess={startProcess}
        onStopProcess={stopProcess}
        onRestartProcess={restartProcess}
        onOpenFile={openFile}
      />
    </div>
  );
}
